import { Delete } from "@mui/icons-material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Autocomplete, Box, Collapse, Container, Icon, IconButton, Radio, Table, TableBody, TableCell, TextField, Tooltip, Typography } from "@mui/material";
import TableRow from '@mui/material/TableRow';
import Grid2 from '@mui/material/Unstable_Grid2';
import CardTotalizador, { CardTotalizadorLabel, CardTotalizadorValue } from "components/cards/CardTotalizador";
import DialogContext from 'components/confirm-dialog-context';
import FloatingActions from "components/floatingactions/FloatingActions";
import InfiniteScrollVirtuoso, { HeaderInfiniteScrollVirtuoso, RowInfiniteScrollVirtuosoProps } from 'components/infinitescrollvirtuoso/InfiniteScrollVirtuoso';
import SkuInput, { SkuInputComponent } from "components/inputs/skuinput/SkuInput";
import { CustomButton } from "components/roundedbutton/CustomButton";
import useCatchApiError from 'hooks/useCatchApiError';
import { useSnackbarCustom } from 'hooks/useSnackCustom';
import { BEEPSOUND } from 'pages/registra-venda/registra-venda';
import { FlowVendaTO, StatusPedido } from "pages/registra-venda/registra-venda.model";
import CustomLoading from "pages/Venda/Header/CustomLoading";
import VendaService from "pages/Venda/venda.service";
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { setDeposito, setTabelaPreco } from "redux/features/configuracoes";
import { setPagamentos } from "redux/features/pagamentoSlice";
import { setStateByProps, setTransportador } from "redux/features/stateSlice";
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { customColors, theme } from "theme";
import { ColumnData, createReadOnly, currencyOf, deepCopy, equalsObj, groupAndMergeArrayItens, hasDuplicateObjects, resolve, roundHalfToEven, sortArray, SpecificTypeKey } from 'utils/util';
import { ICaixaItem, IConferenciaVenda, Itens, ItensPedidoSku } from './conferencia.model';
import ConferenciaService from "./conferencia.service";
import ConferenciaHistorico from "./log/ConferenciaHistorico";

type LocalState = {
    skusConferidos: Itens;
    skusNaoConferidos: ICaixaItem[];
    tableData: [string, ICaixaItem[]][];
    sacolaSelecionada: string;
    sacolasExpandidas: string[];
    qtdConferidaTotal: number;
    valorConferidoTotal: number;
    saldoTotal: number;
    valorSaldoTotal: number;
    loading: boolean;
    openHistoricoDialog: boolean;
};

const initialState: LocalState = {
    skusConferidos: {},
    skusNaoConferidos: [],
    tableData: [],
    sacolaSelecionada: '',
    sacolasExpandidas: [],
    qtdConferidaTotal: 0,
    valorConferidoTotal: 0,
    saldoTotal: 0,
    valorSaldoTotal: 0,
    loading: true,
    openHistoricoDialog: false,
};

export enum ConferenciaRowSituacaoLegenda {
    NAO_CONFERIDO = 'NAO_CONFERIDO',
    CONFERIDO = 'CONFERIDO',
    PARCIAL = 'PARCIAL',
}

export enum ConferenciaRowSituacaoLegendaCor {
    NAO_CONFERIDO = 'var(--pastel)',
    CONFERIDO = 'var(--confereridoBg)',
    PARCIAL = 'var(--confereridoParcialBg)',
}

export enum ConferenciaRowSituacaoLegendaLabel {
    NAO_CONFERIDO = 'Não conferido',
    CONFERIDO = 'Conferido',
    PARCIAL = 'Parcial',
}

export const NAO_CONFERIDOS_KEY_NAME = 'Não conferidos';

type OptionsColumn = { __options: unknown; };

export const getValorUnitarioLiquidoAgrupado = (item: ItensPedidoSku): number => {
    if (item.valorTotalRow === undefined) {
        return item.valorUnitarioLiquido;
    }
    return item.quantidadePedida > 0 ? item.valorTotalRow / item.quantidadePedida : item.valorUnitarioLiquido;
};

export const convertToCaixaItem = (item: ItensPedidoSku, caixa: string, qtdConferida = 0): ICaixaItem => {
    return {
        caixa,
        uuid: '',
        deposito: item.deposito,
        sku: item.sku,
        valorUnitarioLiquido: getValorUnitarioLiquidoAgrupado(item),
        quantidadeConferida: qtdConferida,
        quantidadePedida: item.quantidadePedida,
        quantidadeTotalConferida: item.quantidadeAConferir,
        saldo: item.quantidadePedida
    };
};

const reduceItens = <T extends object>(itens: T[], key: SpecificTypeKey<T, number>) => {
    return itens.reduce((acc, curr) => acc + (curr[key] as number), 0);
};

export const agruparItensPedidoPorSku = (itens: ItensPedidoSku[]): ItensPedidoSku[] => {
    return groupAndMergeArrayItens(itens, {
        keyFn: item => item.sku.uuid,
        mergeFn: (aggregated, current) => ({
            ...aggregated,
            quantidadePedida: aggregated.quantidadePedida + current.quantidadePedida,
            valorTotalRow: roundHalfToEven((aggregated.valorTotalRow ?? aggregated.valorUnitarioLiquido) + current.valorUnitarioLiquido),
        }),
    });
};

export const corrigeValorUnitarioLiquidoConferidos = (skusConferidos: Itens, itensPedido: ItensPedidoSku[]): void => {
    Object.values(skusConferidos).flat().forEach(item => {
        const itemPedido = itensPedido.find(i => i.sku.uuid === item.sku.uuid);
        if (itemPedido) {
            item.valorUnitarioLiquido = getValorUnitarioLiquidoAgrupado(itemPedido);
        }
    });
};

const Conferencia = () => {
    const conferenciaService = ConferenciaService();
    const vendaService = VendaService();
    const [localState, setLocalState] = useState(initialState);
    const { addError } = useSnackbarCustom();
    const { throwErrorMessage } = useCatchApiError();
    const navigate = useNavigate();
    const dialog = useContext(DialogContext);
    const { uuid: uuidPedidoVenda } = useParams();
    const { caixaAberto } = useAppSelector(e => e.caixa.caixaAtual);
    const dispatch = useAppDispatch();

    const conferencia = useRef<IConferenciaVenda | null>(null);
    const itensPedido = useRef<ItensPedidoSku[]>([]);
    const skuinputRef = useRef<SkuInputComponent>(null);


    const columns: ColumnData<ICaixaItem & OptionsColumn>[] = [
        {
            id: 'sku',
            key: 'sku.descritivo',
            header: 'SKU',
            type: 'string',
            width: 54,
            textAlign: 'left',
        },
        {
            id: 'qtdConferida',
            key: 'quantidadeConferida',
            header: 'Quantidade conferida',
            type: 'number',
            width: 14,
            textAlign: 'center',
        },

        {
            id: 'saldo',
            key: 'saldo',
            header: 'Saldo',
            type: 'number',
            width: 14,
            textAlign: 'center',
        },

        {
            id: 'qtdTotal',
            key: 'quantidadePedida',
            header: 'Quantidade do pedido',
            type: 'number',
            width: 14,
            textAlign: 'center',
        },

        {
            id: 'options',
            key: '__options',
            header: '',
            type: 'options',
            width: 4,
            textAlign: 'center',
            convert: (item) => <>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: `100%`
                    }}
                >
                    <IconButton
                        onClick={() => handleDeleteRow(item)}
                        sx={{
                            color: theme.palette.primary.main,
                            marginLeft: '5px',
                        }}
                    >
                        <Delete />
                    </IconButton >
                </Box>
            </>
        },
    ];

    const cards = [
        {
            label: 'Pedido',
            getQtd: () => conferencia.current?.qtdeTotalPedida ?? 0,
            getValor: () => conferencia.current?.valorTotalPedido,
        },
        {
            label: 'Conferido',
            getQtd: () => localState.qtdConferidaTotal,
            getValor: () => localState.valorConferidoTotal,
        },
        {
            label: 'Saldo à conferir',
            getQtd: () => localState.saldoTotal,
            getValor: () => localState.valorSaldoTotal,
        },
    ];

    const handleKeyDown = (e: KeyboardEvent) => {
        const conditionToToggle = document.activeElement?.id.indexOf('acSku') !== -1;
        switch (e.key) {
            case 'l':
            case 'L':
                if (e.altKey) {
                    e.preventDefault();
                    limparConferencia();
                }
                break;
            case 'a':
            case 'A':
                if (e.altKey) {
                    e.preventDefault();
                    addSacola();
                }
                break;

            case 'h':
            case 'H':
                if (e.altKey) {
                    e.preventDefault();
                    toggleHistoricoDialog(true);
                }
                break;
            case 'Enter':
                if (!e.ctrlKey && !conditionToToggle) {
                    BEEPSOUND.play();
                    addError({ message: 'Leitura inválida: o campo não estava selecionado. Por favor, tente novamente.' });
                    resetFocus();
                }

                if (e.ctrlKey) {
                    e.preventDefault();
                    handleIrParaPdv();
                }
                break;
            case 'Escape':
                if (e.ctrlKey) {
                    navigate('/venda');
                }
                break;
        }
    };

    useEffect(() => {
        setLocalState(prev => {
            const itensList = getItensConferidosAsList(prev.skusConferidos);
            const qtdConferidaTotal = reduceItens(itensList, 'quantidadeConferida');
            const valorConferidoTotal = roundHalfToEven(itensList.reduce((prev, acc) => prev + (acc.valorUnitarioLiquido * acc.quantidadeConferida), 0), 2);
            return {
                ...prev,
                qtdConferidaTotal,
                saldoTotal: (conferencia.current?.qtdeTotalPedida ?? 0) - qtdConferidaTotal,
                valorConferidoTotal,
                valorSaldoTotal: (conferencia.current?.valorTotalPedido ?? 0) - valorConferidoTotal,
            };
        });
        resetFocus();
        salvarConferencia();
        //eslint-disable-next-line
        //eslint-disable-next-line
    }, [JSON.stringify(localState.skusConferidos)]);

    const salvarConferencia = () => {
        if (!conferencia.current) return;
        setLocalState(prev => ({ ...prev, loading: true }));
        const conferenciaCopy = deepCopy(conferencia.current);
        conferenciaCopy!.itens = deepCopy(localState.skusConferidos);
        conferenciaService.salvarConferencia(conferenciaCopy)
            .then(() => { })
            .finally(() => {
                setLocalState(prev => ({ ...prev, loading: false }));
            });
    };

    const limparConferencia = () => {
        dialog.confirm({
            error: true,
            title: 'Atenção',
            message: `Todos os dados da conferência serão apagados. Deseja realmente continuar?`,
            onConfirm: () => {
                handleResetAll();
            }
        });
    };

    const atualizaSkusNaoConferidos = () => {
        const novosSkusNaoConferidos = itensPedido.current
            .filter(itemNaoConferido => !getItensConferidosAsList().find(itemConferido => itemConferido.sku.uuid === itemNaoConferido.sku.uuid))
            .map(item => (convertToCaixaItem(item, NAO_CONFERIDOS_KEY_NAME)));
        setLocalState(prev => ({ ...prev, skusNaoConferidos: novosSkusNaoConferidos }));
    };

    useEffect(atualizaSkusNaoConferidos,
        //eslint-disable-next-line
        [JSON.stringify(localState.skusConferidos), localState.sacolaSelecionada]
    );

    const atualizaTableData = () => {
        const tableData = sortArray(Object.entries(localState.skusConferidos), { fieldToSort: '0' });
        if (!!localState.skusNaoConferidos.length) {
            tableData.push([NAO_CONFERIDOS_KEY_NAME, localState.skusNaoConferidos]);
        }
        setLocalState(prev => ({ ...prev, tableData }));
    };

    useEffect(atualizaTableData,
        //eslint-disable-next-line
        [JSON.stringify(localState.skusConferidos), JSON.stringify(localState.skusNaoConferidos)]);

    const getItensConferidosAsList = (produtos: Itens = localState.skusConferidos): ICaixaItem[] => {
        return Object.values(produtos).flat();
    };

    const getUUIDPedidoVenda = (): string => {
        if (!uuidPedidoVenda) {
            navigate('/vendas');
            throw new Error('uuidPedidoVenda ausente');
        }
        return uuidPedidoVenda;
    };

    useEffect(() => {
        handleResetAll();
        loadConferencia();

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
        //eslint-disable-next-line
    }, []);


    const loadConferencia = () => {
        conferenciaService.getConferir(getUUIDPedidoVenda())
            .then(({ data }) => {
                handleResetAll();
                conferencia.current = createReadOnly(data);
                conferenciaService.getItensPedido(getUUIDPedidoVenda())
                    .then(({ data }) => {
                        itensPedido.current = createReadOnly(agruparItensPedidoPorSku(data.itensPedidos));
                        let skusConferidos = deepCopy(conferencia.current!.itens);
                        if (equalsObj(skusConferidos, {})) {
                            skusConferidos = { 'Sacola 1': [] };
                        }
                        corrigeValorUnitarioLiquidoConferidos(skusConferidos, itensPedido.current);
                        const caixas = Object.keys(skusConferidos);
                        const sacolaSelecionada = caixas.sort()[0];

                        setLocalState(prev => {
                            const skus = getItensConferidosAsList(skusConferidos);
                            const { itensChecados } = hasDuplicateObjects(skus, (a, b) => a.sku === b.sku);
                            itensChecados.forEach(s => atualizarSaldoGeral(s.sku.uuid, skusConferidos));
                            return { ...prev, skusConferidos, sacolaSelecionada, sacolasExpandidas: caixas, loading: false };
                        });

                        resetFocus();
                    });
            })
            .catch(e => {
                throwErrorMessage(e);
            });
    };

    const handleResetAll = () => {
        setLocalState(prev => ({ ...prev, skusConferidos: { 'Sacola 1': [] } }));
        resetFocus();
    };

    const resetFocus = useCallback(() => {
        skuinputRef.current?.focus();
    }, []);

    const getQuantidadePedidaTotal = (skuUUID: string): number => {
        return reduceItens(itensPedido.current.filter(i => i.sku.uuid === skuUUID), 'quantidadePedida');
    };

    const atualizarSaldoGeral = (skuUUID: string, skusConferidos: Itens) => {
        const skus = getItensConferidosAsList(skusConferidos).filter(s => s.sku.uuid === skuUUID);
        const qtdPedida = getQuantidadePedidaTotal(skuUUID);
        const qtdConferidaGeral = reduceItens(skus, 'quantidadeConferida');
        const saldoGeral = qtdPedida - qtdConferidaGeral;
        skus.forEach(s => {
            s.saldo = saldoGeral;
            s.quantidadePedida = qtdPedida;
        });
    };

    const onSelectProduto = (value: ItensPedidoSku | null, isAdding: boolean) => {
        if (!value) {
            BEEPSOUND.play();
            return addError({ message: 'SKU não faz parte do pedido' });
        }

        setLocalState(prev => {
            const sacolaSelecionada = prev.sacolaSelecionada;
            const skusConferidos = prev.skusConferidos;
            const itens = skusConferidos[sacolaSelecionada] ?? [];
            const itemExisteNaSacola = itens.find(item => item.sku.uuid === value.sku.uuid);
            const itemExistemEmOutraSacola = getItensConferidosAsList(skusConferidos)
                .find(item => item.sku.uuid === value.sku.uuid);


            if (isAdding) {
                const semSaldoDisponivel = (itemExisteNaSacola && itemExisteNaSacola.saldo <= 0) || (itemExistemEmOutraSacola && itemExistemEmOutraSacola.saldo <= 0);
                if (semSaldoDisponivel) {
                    BEEPSOUND.play();
                    addError({ message: 'Saldo insuficiente para conferir' });
                    return prev;
                }

                if (itemExisteNaSacola) {
                    itemExisteNaSacola.quantidadeConferida += 1;
                } else {
                    const toPush: ICaixaItem = convertToCaixaItem(value, sacolaSelecionada, 1);
                    skusConferidos[sacolaSelecionada] = [...itens, toPush];
                }

            } else if (itemExisteNaSacola) {
                itemExisteNaSacola.quantidadeConferida -= 1;
                if (itemExisteNaSacola.quantidadeConferida === 0) {
                    skusConferidos[sacolaSelecionada] = itens.filter(s => s.sku.uuid !== value.sku.uuid);
                }
            } else {
                BEEPSOUND.play();
                addError({ message: 'SKU não encontrado na sacola! Por favor, verifique.' });
                return prev;
            }
            atualizarSaldoGeral(value.sku.uuid, skusConferidos);
            return {
                ...prev,
                skusConferidos,
            };
        });
    };

    const addSacola = () => {
        setLocalState(prev => {
            const obj = prev.skusConferidos;
            const sacolasExistentes = Object.keys(obj)
                .map(key => parseInt(key.replace('Sacola ', '')))
                .sort((a, b) => a - b);

            let novoNumero = 1;
            for (let i = 0; i < sacolasExistentes.length; i++) {
                if (sacolasExistentes[i] !== novoNumero) {
                    break;
                }
                novoNumero++;
            }

            const nomeSacola = `Sacola ${novoNumero}`;
            obj[nomeSacola] = [];
            return {
                ...prev,
                skusConferidos: obj,
                sacolasExpandidas: [...prev.sacolasExpandidas, nomeSacola],
                sacolaSelecionada: nomeSacola
            };
        });
        resetFocus();
    };

    const handleChangeCaixa = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalState(prev => ({ ...prev, sacolaSelecionada: event.target.value }));
        resetFocus();
    };

    const handleDeleteRow = (item: ICaixaItem) => {
        dialog.confirm({
            title: 'Atenção',
            message: `Deseja excluir a ${item.sku.descritivo}?`,
            onConfirm: () => {
                setLocalState(prev => {
                    const skusConferidos = prev.skusConferidos;
                    skusConferidos[item.caixa] = skusConferidos[item.caixa].filter(s => s.sku.uuid !== item.sku.uuid);
                    atualizarSaldoGeral(item.sku.uuid, skusConferidos);
                    return {
                        ...prev,
                        skusConferidos
                    };
                });
            }
        });
    };

    const handleIrParaPdv = () => {
        const hasSkusConferidosComSaldo = getItensConferidosAsList().some(s => s.saldo !== 0);
        const hasSkusNaoConferidos = !!localState.skusNaoConferidos.length;
        const isParcial = hasSkusConferidosComSaldo || hasSkusNaoConferidos;
        if (isParcial) {
            dialog.confirm({
                title: 'Conferência parcial',
                message: `Deseja continuar sem concluir a conferência?`,
                onConfirm: () => {
                    navegaPDV();
                }
            });
        } else {
            navegaPDV();
        }
    };

    const navegaPDV = () => {
        setLocalState(prevState => ({ ...prevState, loading: true }));
        if (!caixaAberto) {
            setLocalState(prevState => ({ ...prevState, loading: false }));
            return addError({
                message: `Abra um caixa para movimentar o pedido!`,
                closeable: true
            });
        }
        return vendaService.carregaPedido(getUUIDPedidoVenda())
            .then(({ data }: { data: FlowVendaTO; }) => {
                setLocalState(prevState => ({ ...prevState, loading: false }));
                if (data.statusPedido === StatusPedido.ABERTO.toString().toUpperCase()) {
                    if (data.tabelaPreco) dispatch(setTabelaPreco(data.tabelaPreco));
                    if (data.deposito) dispatch(setDeposito(data.deposito));
                    if (data.transportador) dispatch(setTransportador(data.transportador));
                    data.itens.forEach(e => e.estoque = -1);
                    dispatch(setStateByProps(data));
                    dispatch(setPagamentos({ itens: data.itensPagamento ?? [], itensTroca: data.itensPagamentoDevolucao ?? [] }));
                    setTimeout(() => {
                        navigate("/registra-venda", { state: true });
                    }, 200);
                    return;
                }
                addError({
                    message: `O pedido não está mais aberto!`,
                    closeable: true
                });
            })
            .catch(e => {
                throwErrorMessage(e);
            });
    };

    const toggleHistoricoDialog = (open: boolean) => {
        setLocalState(prev => ({ ...prev, openHistoricoDialog: open }));
        resetFocus();
    };

    const TableCustomRow = ({ calcWidth, columns, data, index }: RowInfiniteScrollVirtuosoProps<[string, ICaixaItem[]], ICaixaItem & OptionsColumn>) => {
        const [caixa, itens] = data;
        const resolveWrapper = (data: any, key: string) => resolve(data, key);

        const isNaoConferidos = () => {
            return caixa === NAO_CONFERIDOS_KEY_NAME;
        };

        const isOpen = () => {
            return isNaoConferidos() ? true : localState.sacolasExpandidas.includes(caixa);
        };

        const toggleOpen = () => {
            setLocalState(prev => {
                let sacolasExpandidas = prev.sacolasExpandidas;
                const prevOpen = sacolasExpandidas.includes(caixa);
                if (!prevOpen) sacolasExpandidas = [...sacolasExpandidas, caixa];
                else sacolasExpandidas = sacolasExpandidas.filter(c => c !== caixa);
                return { ...prev, sacolasExpandidas: sacolasExpandidas };
            });
            resetFocus();
        };

        const getColorRow = (item: ICaixaItem) => {
            if (isNaoConferidos()) return;
            if (item.saldo === 0) return customColors.confereridoBg;
            if (item.quantidadePedida > item.quantidadeConferida) return customColors.confereridoParcialBg;
        };

        const shuldShowColumn = (index: number) => {
            if (!isNaoConferidos()) return true;
            const isFirst = index === 0;
            const isPenultimo = index === columns.length - 2;
            return isFirst || isPenultimo;
        };

        const handleDeleteSacola = () => {
            dialog.confirm({
                title: 'Atenção',
                message: `Deseja excluir o sku ${caixa}?`,
                onConfirm: () => {
                    setLocalState(prev => {
                        const skusConferidos = prev.skusConferidos;
                        delete skusConferidos[caixa];

                        if (equalsObj(skusConferidos, {})) {
                            skusConferidos['Sacola 1'] = [];
                        }

                        const sacolasExistentes = Object.keys(skusConferidos)
                            .map(key => parseInt(key.replace('Sacola ', '')))
                            .sort((a, b) => a - b);
                        return {
                            ...prev,
                            skusConferidos,
                            sacolasExpandidas: prev.sacolasExpandidas.filter(s => s !== caixa),
                            sacolaSelecionada: `Sacola ${sacolasExistentes[0]}`,
                        };
                    });
                    resetFocus();
                }
            });
        };

        return (
            <TableCell
                colSpan={999}
                style={{ padding: 0 }}
            >
                <Table>
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={999} sx={{ padding: 0, lineHeight: '30px' }}>
                                <Box display={'flex'} alignItems={'center'} sx={{
                                    height: '40px',
                                    paddingLeft: `25px`,
                                }}>
                                    {!isNaoConferidos() &&
                                        <>
                                            <Radio
                                                checked={localState.sacolaSelecionada === caixa}
                                                onChange={handleChangeCaixa}
                                                value={caixa}
                                                name="radio-buttons"
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                    '&.Mui-checked': {
                                                        color: theme.palette.primary.main,
                                                    },
                                                }}
                                            />
                                            <IconButton
                                                aria-label="expand row"
                                                size="small"
                                                onClick={toggleOpen}
                                            >
                                                {isOpen() ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                            </IconButton>
                                        </>
                                    }

                                    <Typography
                                        fontWeight={600}
                                        textAlign={'left'}
                                        sx={{
                                            width: '100%'
                                        }}
                                    >
                                        {caixa}
                                    </Typography>

                                    {!isNaoConferidos() &&
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                width: calcWidth(columns[4].width, 15)
                                            }}
                                        >

                                            <IconButton
                                                onClick={() => handleDeleteSacola()}
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                }}
                                            >
                                                <Delete />
                                            </IconButton >
                                        </Box>
                                    }
                                </Box>
                            </TableCell>
                        </TableRow>

                        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                            <TableCell style={{ padding: 0 }} colSpan={999}>
                                <Collapse in={isOpen()} timeout="auto" unmountOnExit>
                                    <Table size="small" aria-label="purchases">
                                        <TableBody>
                                            {itens.map((item, indexRow) => {
                                                return <TableRow
                                                    key={item.caixa + "-" + item.sku.uuid + "-" + indexRow + "-row"}
                                                    sx={{
                                                        background: getColorRow(item),
                                                    }}
                                                >
                                                    {columns.map((col, indexCol) => {
                                                        return <TableCell
                                                            key={col.key + index + indexRow + indexCol + '-row'}
                                                            sx={{
                                                                height: '30px',
                                                                minWidth: col.minWidth,
                                                                maxWidth: col.maxWidth,
                                                                width: calcWidth(col.width, 15),
                                                                textAlign: col.textAlign,
                                                                fontSize: '14px',
                                                                padding: 0,
                                                                paddingLeft: '15px',
                                                                color: item.saldo === 0 ? theme.palette.background.default : theme.palette.common.black,
                                                                borderBottom: indexRow === itens.length - 1 ? 'unset' : undefined,
                                                            }}
                                                        >
                                                            {
                                                                shuldShowColumn(indexCol) && (col.convert?.(item as any, { column: col, extraData: { calcWidth } }) ?? resolveWrapper(item, col.key))
                                                            }
                                                        </TableCell>;
                                                    })}
                                                </TableRow>;
                                            })}

                                            {!itens.length && <TableRow>
                                                <TableCell
                                                    sx={{
                                                        height: '25px',
                                                        padding: 0,
                                                        paddingLeft: '15px',
                                                        color: theme.palette.common.black,
                                                        borderBottom: 'unset',
                                                    }}
                                                >
                                                    <Typography fontSize='12px'>Nenhum item adicionado</Typography>
                                                </TableCell>
                                            </TableRow>}
                                        </TableBody>
                                    </Table>
                                </Collapse>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableCell>
        );
    };

    return (
        <Container
            maxWidth="xl"
            sx={{
                margin: '20px auto',
                display: 'grid',
                gap: '20px',
            }}>
            <Grid2 container spacing={2}>
                <Grid2 xs={12} sm={6} lg={6} >
                    <Autocomplete
                        disabled={true}
                        value={conferencia.current?.clienteDescritivo ?? 'CONSUMIDOR'}
                        id="cliente"
                        options={[]}
                        forcePopupIcon={false}
                        renderInput={
                            (params) =>
                                <TextField {...params}
                                    label="Cliente"
                                    variant="standard"
                                    InputLabelProps={{
                                        sx: {
                                            marginLeft: '5px'
                                        }
                                    }}
                                />}
                    />
                </Grid2>

                <Grid2 xs={12} sm={6} lg={6} >
                    <Autocomplete
                        disabled={true}
                        value={conferencia.current?.vendedorDescritivo ?? 'SEM VENDEDOR'}
                        id="vendedor"
                        options={[]}
                        forcePopupIcon={false}
                        renderInput={
                            (params) =>
                                <TextField {...params}
                                    label="Vendedor Principal"
                                    variant="standard"
                                    InputLabelProps={{
                                        sx: {
                                            marginLeft: '5px'
                                        }
                                    }}
                                />}
                    />
                </Grid2>

                <Grid2 xs={12} sm={6} lg={6}>
                    <SkuInput
                        ref={skuinputRef}
                        customOptions={itensPedido.current}
                        onSelect={onSelectProduto}
                        getKey={s => s.sku.uuid}
                        getOptionLabel={s => s.sku.descritivo}
                    />
                </Grid2>

                <Grid2 xs={12} sm={6} lg={6} >
                    <Tooltip
                        title={'Adicionar sacola (Alt + A)'}
                    >
                        <IconButton
                            onClick={addSacola}
                            sx={{ transform: 'translateY(5px)', color: theme.palette.primary.main }}
                        >
                            <Icon className='fa-solid fa-bag-shopping' sx={{ fontSize: '35px' }}></Icon>
                        </IconButton>
                    </Tooltip>
                </Grid2>

            </Grid2>

            <Grid2 container spacing={2}>

                <Grid2 xs={12} height="auto">
                    <InfiniteScrollVirtuoso
                        data={localState.tableData}
                        heightScale={'465px'}
                        renderItem={(tableProps) => TableCustomRow({ ...tableProps, columns })}
                        fixedHeaderContent={(tableProps) => HeaderInfiniteScrollVirtuoso({ ...tableProps, columns })}
                    />
                </Grid2>
            </Grid2>

            <Grid2 container spacing={2} sx={{ textAlign: "center" }}>

                {cards.map((c, i) => {
                    return <Grid2 key={i} xs={12} sm={6} md={4} lg={4}>
                        <CardTotalizador
                            id="abc"
                        >
                            <CardTotalizadorValue
                                id={c.label + '-qtd'}
                                value={c.getQtd()}
                                color={theme.palette.primary.main}
                            />

                            <CardTotalizadorValue
                                id={c.label + '-valor'}
                                value={currencyOf(c.getValor())}
                                color={theme.palette.primary.main}
                                style={{ fontSize: '16px' }}
                            />

                            <CardTotalizadorLabel
                                id={c.label}
                                label={c.label}
                            />
                        </CardTotalizador>
                    </Grid2>;
                })}
            </Grid2>

            <Grid2 container spacing={2} sx={{ textAlign: "center", display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{
                    display: 'flex',
                    maxHeight: '50px',
                    position: 'relative',
                    gap: '10px',
                    alignItems: 'flex-end',
                }}>
                    <CustomButton
                        variant="contained"
                        startIcon={<Icon className='fa-solid fa-arrow-left' sx={{ width: '25px' }} />}
                        containerStyle={{
                            boxShadow: "2px 2px 10px rgba(0,0,0,0.35)"
                        }}
                        color="error"
                        onClick={() => { navigate('/venda'); }}
                    >
                        Voltar
                    </CustomButton>

                    {Object.values(ConferenciaRowSituacaoLegenda).map(key => {
                        return <Box key={`legenda-${key}`}>
                            <Box sx={{
                                height: '20px',
                                width: '105px',
                                bgcolor: ConferenciaRowSituacaoLegendaCor[key],
                                border: '1px solid grey',
                                borderRadius: '5px'
                            }} >
                            </Box>
                            <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
                                {ConferenciaRowSituacaoLegendaLabel[key]}
                            </Typography>
                        </Box>;
                    })}
                </Box>

                <Box sx={{
                    display: 'flex',
                    maxHeight: '50px',
                    position: 'relative',
                    gap: '10px',

                    alignItems: 'flex-end',
                }}>
                    <CustomButton
                        variant="contained"
                        startIcon={<Icon className='fa-solid fa-arrow-right' sx={{ width: '25px' }} />}
                        containerStyle={{
                            boxShadow: "2px 2px 10px rgba(0,0,0,0.35)"
                        }}
                        color='success'
                        onClick={handleIrParaPdv}
                    >
                        Ir para PDV
                    </CustomButton>

                    <FloatingActions
                        actions={[
                            {
                                icon: 'fa-solid fa-eraser',
                                label: 'Limpar conferência (Alt + L)',
                                onClick: limparConferencia,
                                id: 'limpar-conferencia'
                            },

                            {
                                icon: 'fa-solid fa-eraser',
                                label: 'Histórico (Alt + H)',
                                onClick: () => toggleHistoricoDialog(true),
                                id: 'abrir-historico'
                            },
                        ]}
                        onClose={(r) => {
                            if (['blur', 'toggle'].includes(r)) {
                                resetFocus();
                            }
                        }}
                    />
                </Box>
            </Grid2>

            {localState.loading && <CustomLoading />}

            <ConferenciaHistorico
                open={localState.openHistoricoDialog}
                onCloseHistoricoDialog={() => toggleHistoricoDialog(false)}
                uuidPedido={getUUIDPedidoVenda()}
            />
        </Container>
    );
};

export default Conferencia;
